import { useEffect, useState } from "react"
import { Link, NavLink, Outlet, useParams } from "react-router-dom"
import {
	ChevronDown,
	CreditCard,
	FolderPlus,
	HardDrive,
	LayoutDashboard,
	ListChecks,
	LogOut,
	Menu,
	PieChart,
	Settings,
	Tags,
	UsersRound,
	X
} from "lucide-react"
import { useApp } from "../../state/AppState"
import { useApplyTheme } from "../../state/theme"
import { accent } from "../../design/palette"
import { Avatar, Button, cx, IconButton, Popover, ToastStack } from "../ui"
import { fileSize } from "../../lib/format"
import { NewProjectDialog } from "../../pages/NewProjectDialog"
import { Logo } from "../brand/Logo"
import { IssueRail } from "./IssueRail"

export function AppShell() {
	const { projects, toasts, dismissToast } = useApp()
	const { projectId, issueId } = useParams()
	const [navOpen, setNavOpen] = useState(false)
	const [creating, setCreating] = useState(false)
	useApplyTheme()

	// Close the mobile drawer whenever the route changes underneath it.
	useEffect(() => {
		setNavOpen(false)
	}, [projectId])

	return (
		<div className="flex min-h-screen bg-ink-50">
			{navOpen && (
				<div
					className="keep-light fixed inset-0 z-30 bg-ink-950/40 lg:hidden"
					onClick={() => setNavOpen(false)}
					aria-hidden
				/>
			)}

			<Sidebar
				projects={projects}
				activeProjectId={projectId}
				open={navOpen}
				onClose={() => setNavOpen(false)}
				onNewProject={() => setCreating(true)}
			/>

			{projectId && <IssueRail projectId={projectId} activeIssueId={issueId} />}

			<div className="flex min-w-0 flex-1 flex-col">
				<TopBar onOpenNav={() => setNavOpen(true)} />
				<main className="flex-1 px-4 py-5 sm:px-6 lg:px-8">
					<div className="mx-auto w-full max-w-6xl">
						<Outlet />
					</div>
				</main>
			</div>

			<NewProjectDialog open={creating} onClose={() => setCreating(false)} />
			<ToastStack toasts={toasts} onDismiss={dismissToast} />
		</div>
	)
}

/* -------------------------------------------------------------------------- */

function Sidebar({ projects, activeProjectId, open, onClose, onNewProject }) {
	const { user } = useApp()
	const active = projects.find((p) => p.project_id === activeProjectId)

	return (
		<aside
			className={cx(
				// Dark by design in either theme.
				"keep-light fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col",
				"bg-gradient-to-b from-brand-950 via-brand-900 to-brand-800 text-brand-100",
				// Pinned to the viewport on desktop: a long page scrolls past it
				// rather than stretching it, and the project list scrolls inside.
				"transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0",
				open ? "translate-x-0" : "-translate-x-full"
			)}
		>
			<div className="flex items-center justify-between px-4 py-4">
				<Link to="/app" aria-label="Loomina" className="flex text-white">
					<Logo size="sm" />
				</Link>
				<IconButton
					label="Close navigation"
					icon={X}
					onClick={onClose}
					className="text-brand-200 hover:bg-white/10 hover:text-white lg:hidden"
				/>
			</div>

			<nav className="flex-1 overflow-y-auto px-2.5 pb-4 scrollbar-slim">
				<NavItem to="/app" end icon={LayoutDashboard}>
					Dashboard
				</NavItem>
				{/* Always present. Whether it is included in the plan is decided
				    on the page itself, so the feature is discoverable rather than
				    invisible to the people who would pay for it. */}
				<NavItem to="/teams" icon={UsersRound}>
					Teams
				</NavItem>

				<div className="mb-1.5 mt-5 flex items-center justify-between px-2.5">
					<p className="text-2xs font-semibold uppercase tracking-wider text-brand-300">
						Projects
					</p>
					<IconButton
						label="New project"
						icon={FolderPlus}
						size={14}
						onClick={onNewProject}
						className="h-6 w-6 text-brand-200 hover:bg-white/10 hover:text-white"
					/>
				</div>

				<div className="space-y-0.5">
					{projects.map((project) => (
						<ProjectNavItem
							key={project.project_id}
							project={project}
							active={project.project_id === activeProjectId}
						/>
					))}
					{projects.length === 0 && (
						<p className="px-2.5 py-2 text-xs leading-relaxed text-brand-300">
							No projects yet. Create one to start capturing clarifications.
						</p>
					)}
				</div>

				{active && (
					<div className="mt-5">
						<p className="mb-1.5 truncate px-2.5 text-2xs font-semibold uppercase tracking-wider text-brand-300">
							{active.name}
						</p>
						<div className="space-y-0.5">
							<NavItem to={`/projects/${active.project_id}`} end icon={ListChecks}>
								Issues
								<span className="ml-auto flex items-center gap-1">
									<AssignedBadge count={active.assigned_to_me} />
									<span className="rounded-full bg-white/10 px-1.5 text-2xs font-semibold tabular">
										{active.open_count}
									</span>
								</span>
							</NavItem>
							<NavItem to={`/projects/${active.project_id}/team`} icon={UsersRound}>
								Team
							</NavItem>
							<NavItem to={`/projects/${active.project_id}/tags`} icon={Tags}>
								Tags
							</NavItem>
							<NavItem to={`/projects/${active.project_id}/insights`} icon={PieChart}>
								Insights
							</NavItem>
						</div>
					</div>
				)}
			</nav>

			{user && <QuotaMeter user={user} />}
		</aside>
	)
}

function NavItem({ to, end, icon: Icon, children }) {
	return (
		<NavLink
			to={to}
			end={end}
			className={({ isActive }) =>
				cx(
					"flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[0.8125rem] font-medium transition-colors",
					isActive
						? "bg-white/15 text-white"
						: "text-brand-100/80 hover:bg-white/10 hover:text-white"
				)
			}
		>
			<Icon size={15} className="shrink-0" aria-hidden />
			{children}
		</NavLink>
	)
}

function ProjectNavItem({ project, active }) {
	const tone = accent(project.colour)
	return (
		<NavLink
			to={`/projects/${project.project_id}`}
			className={cx(
				"flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[0.8125rem] transition-colors",
				active
					? "bg-white/15 font-medium text-white"
					: "text-brand-100/80 hover:bg-white/10 hover:text-white"
			)}
		>
			<span className={cx("h-2 w-2 shrink-0 rounded-full", tone.dot)} aria-hidden />
			<span className="min-w-0 flex-1 truncate">{project.name}</span>
			<span className="flex shrink-0 items-center gap-1">
				<AssignedBadge count={project.assigned_to_me} />
				{project.open_count > 0 && (
					<span className="rounded-full bg-white/10 px-1.5 text-2xs font-semibold tabular">
						{project.open_count}
					</span>
				)}
			</span>
		</NavLink>
	)
}

// Open issues waiting on the signed-in user, shown beside the open count.
function AssignedBadge({ count }) {
	if (!count) return null
	return (
		<span
			className="shrink-0 rounded-full bg-red-700 px-1.5 text-2xs font-semibold text-white tabular"
			title={`${count} assigned to you`}
		>
			{count}
		</span>
	)
}

function QuotaMeter({ user }) {
	const usage = user.usage || {}
	const unlimited = usage.bytes_limit < 0
	const pct = unlimited ? 0 : Math.min(100, usage.percent_used || 0)
	const tight = pct >= 80

	return (
		<Link
			to="/settings/billing"
			className="block border-t border-white/10 px-4 py-3 transition-colors hover:bg-white/5"
		>
			<div className="flex items-center gap-1.5 text-2xs text-brand-200">
				<HardDrive size={12} aria-hidden />
				<span>{user.plan?.name} plan</span>
				{!unlimited && <span className="ml-auto tabular">{Math.round(pct)}%</span>}
			</div>
			{!unlimited && (
				<div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/15">
					<div
						className={cx("h-full rounded-full", tight ? "bg-amber-400" : "bg-brand-300")}
						style={{ width: `${Math.max(2, pct)}%` }}
					/>
				</div>
			)}
			<p className="mt-1.5 text-2xs text-brand-300">
				{fileSize(usage.bytes_used)}
				{unlimited ? " used" : ` of ${fileSize(usage.bytes_limit)} used`}
			</p>
		</Link>
	)
}

/* -------------------------------------------------------------------------- */

function TopBar({ onOpenNav }) {
	return (
		<header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-ink-200 bg-white/85 px-4 backdrop-blur sm:px-6 lg:px-8">
			<IconButton
				label="Open navigation"
				icon={Menu}
				onClick={onOpenNav}
				className="-ml-1.5 lg:hidden"
			/>
			<div className="ml-auto">
				<AccountMenu />
			</div>
		</header>
	)
}

function AccountMenu() {
	const { user, signOut, timezone } = useApp()

	return (
		<Popover
			className="w-64"
			trigger={({ toggle, open }) => (
				<button
					type="button"
					onClick={toggle}
					aria-expanded={open}
					className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 text-left transition-colors hover:bg-ink-100"
				>
					<Avatar person={user} size="md" />
					<span className="hidden min-w-0 sm:block">
						<span className="block truncate text-[0.8125rem] font-medium leading-tight text-ink-900">
							{user?.name}
						</span>
						<span className="block truncate text-2xs leading-tight text-ink-500">
							{[user?.role, user?.company].filter(Boolean).join(", ") || user?.email}
						</span>
					</span>
					<ChevronDown size={14} className="shrink-0 text-ink-400" aria-hidden />
				</button>
			)}
		>
			{({ close }) => (
				<>
					<div className="border-b border-ink-100 px-2.5 pb-2 pt-1.5">
						<p className="truncate text-[0.8125rem] font-medium text-ink-900">
							{user?.name}
						</p>
						<p className="truncate text-2xs text-ink-500">{user?.email}</p>
						<p className="mt-1 text-2xs text-ink-400">Times shown in {timezone}</p>
					</div>

					<MenuLink to="/settings/profile" icon={Settings} onClick={close}>
						Settings
					</MenuLink>
					<MenuLink to="/settings/billing" icon={CreditCard} onClick={close}>
						Plan and billing
						{user?.plan?.key !== "free" && (
							<span className="ml-auto rounded-full bg-brand-100 px-1.5 text-2xs font-semibold text-brand-800">
								{user?.plan?.name}
							</span>
						)}
					</MenuLink>

					<div className="mt-1 border-t border-ink-100 pt-1">
						<button
							type="button"
							onClick={() => {
								close()
								signOut()
							}}
							className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[0.8125rem] text-ink-700 transition-colors hover:bg-ink-100"
						>
							<LogOut size={15} className="shrink-0 text-ink-400" aria-hidden />
							Sign out
						</button>
					</div>
				</>
			)}
		</Popover>
	)
}

function MenuLink({ to, icon: Icon, onClick, children }) {
	return (
		<Link
			to={to}
			onClick={onClick}
			className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[0.8125rem] text-ink-700 transition-colors hover:bg-ink-100"
		>
			<Icon size={15} className="shrink-0 text-ink-400" aria-hidden />
			{children}
		</Link>
	)
}

export { Button }
